import { SettingsService } from './services/settings.service';
import { AuthService } from './auth/service/auth.service';
import { Component, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { NavigationEnd, Router } from '@angular/router';
import { forkJoin, interval, Subscription } from 'rxjs';
import { AnalyticsService } from './services/analytics.service';
import { UpdateService } from './services/update.service';
import { environment } from './../environments/environment';
import {
  NgcCookieConsentService,
  NgcInitializingEvent,
  NgcNoCookieLawEvent,
  NgcStatusChangeEvent,
} from 'ngx-cookieconsent';
import { ViewportScroller } from '@angular/common';
import { slideInAnimation, fadeInAnimation } from './route-animations';
import { UserAchievement } from './models/user-achievement.model';
import { AchievementService } from './services/achievement.service';
import { UserService } from './auth/service/user.service';
import { UserTaskStatusService } from './services/user-task-status.service';
import moment from 'moment';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    animations: [slideInAnimation, fadeInAnimation],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AppComponent implements OnInit, OnDestroy {
  title = 'base-pwa'; // keep refs to subscriptions to be able to unsubscribe later
  public innerWidth!: number;
  private popupOpenSubscription!: Subscription;
  private popupCloseSubscription!: Subscription;
  private initializeSubscription!: Subscription;
  private statusChangeSubscription!: Subscription;
  private revokeChoiceSubscription!: Subscription;
  private noCookieLawSubscription!: Subscription;
  private checkSiteStatusInterval: any;
  private siteStatus = 1;
  public activeAchievement!: UserAchievement;
  public showAward = false;
  public userData: any;
  public currentUser: any;
  public gettingStartedCompleted = false;

  notifications: UserAchievement[] = [];
  notificationsInterval: any;

  constructor(
    private sw: UpdateService,
    private router: Router,
    private titleService: Title,
    private ccService: NgcCookieConsentService,
    private analyticsService: AnalyticsService,
    private authService: AuthService,
    private viewportScroller: ViewportScroller,
    private settingsService: SettingsService,
    private achievementService: AchievementService,
    private userService: UserService,
    private userTaskStatusService: UserTaskStatusService
  ) {
    // check the service worker for updates
    this.sw.checkForUpdates();
  }

  ngOnInit() {
    this.checkSiteStatus();
    this.checkSiteStatusInterval = interval(60000).subscribe((count) => {
      this.checkSiteStatus();
    });
    // try and restpore session
    this.authService.restore().subscribe(
      (restoreResponsedata) => {
        if (this.router.url.split('?')[0] === '/login') {
          if (this.authService.redirect) {
            this.router.navigateByUrl(this.authService.redirect);
          } else {
            this.router.navigate(['/']);
          }
        }
      },
      (errorMessage) => {
        // try autologin here?
        this.authService.autoLogin()?.subscribe(
          (resData) => {
            if (this.router.url.split('?')[0] === '/login') {
              if (this.authService.redirect) {
                this.router.navigateByUrl(this.authService.redirect);
              } else {
                this.router.navigate(['/dashboard']);
              }
            }
          },
          (errorMessage) => {
            // console.log(errorMessage);
          }
        );
      }
    );

    this.userService.userData.subscribe((userData) => {
      if (userData) {
        this.userData = userData;
        this.checkExpired();
      } else {
        this.userData = null;
        this.gettingStartedCompleted = false;
      }
    });

    this.authService.user.subscribe((user) => {
      if (user) {
        this.currentUser = user;
        // check for notifications
        this.notificationsInterval = null;
        this.notificationsInterval = interval(60000).subscribe((count) => {
          this.checkNotifications();
        });
        this.achievementService.updateNotifications.subscribe((data) => {
          if (data) {
            this.checkNotifications();
          }
        });
        // end notifications
        this.checkNotifications();
      } else {
        // this.achievementService.updateNotifications.unsubscribe();
        this.notificationsInterval = null;
        this.gettingStartedCompleted = false;
      }
    });

    // scroll to top on route change
    this.router.events.subscribe((evt) => {
      if (!(evt instanceof NavigationEnd)) {
        return;
      }
      if (this.siteStatus === 0 && this.router.url != '/offline') {
        this.router.navigate(['/offline']);
      }
      this.checkGettingStarted();
      this.checkExpired();
      window.scrollTo(0, 0);

      // set page title
      const title = this.getTitle(
        this.router.routerState,
        this.router.routerState.root
      ).join(' | ');
      this.titleService.setTitle(environment.title + ' | ' + title);
      this.analyticsService.trackVirtualPageview(
        environment.title + ' | ' + title
      );
    });
    // cookie consent

    if (this.ccService.hasConsented()) {
      // start tracking
      this.analyticsService.startTracking();
    }
    // subscribe to cookieconsent observables to react to main events
    this.popupOpenSubscription = this.ccService.popupOpen$.subscribe(() => {
      // you can use this.ccService.getConfig() to do stuff...
    });

    this.popupCloseSubscription = this.ccService.popupClose$.subscribe(() => {
      // you can use this.ccService.getConfig() to do stuff...
    });

    this.initializeSubscription = this.ccService.initializing$.subscribe(
      (event: NgcInitializingEvent) => {
        // you can use this.ccService.getConfig() to do stuff...
      }
    );

    this.statusChangeSubscription = this.ccService.statusChange$.subscribe(
      (event: NgcStatusChangeEvent) => {
        // you can use this.ccService.getConfig() to do stuff...
        if (event.status === 'deny') {
          this.analyticsService.stopTracking();
        } else {
          this.analyticsService.startTracking();
        }
      }
    );

    this.revokeChoiceSubscription = this.ccService.revokeChoice$.subscribe(
      () => {
        // you can use this.ccService.getConfig() to do stuff...
      }
    );

    this.noCookieLawSubscription = this.ccService.noCookieLaw$.subscribe(
      (event: NgcNoCookieLawEvent) => {
        // you can use this.ccService.getConfig() to do stuff...
      }
    );
    // end cookie consent
  }

  getTitle(state: any, parent: any): any {
    const data = [];
    if (parent && parent.snapshot.data && parent.snapshot.data.pageTitle) {
      data.push(parent.snapshot.data.pageTitle);
    }

    if (parent && parent.snapshot.data && parent.snapshot.data.subTitle) {
      data.push(parent.snapshot.data.subTitle);
    }

    if (state && parent) {
      data.push(...this.getTitle(state, state.firstChild(parent)));
    }
    return data;
  }

  onResize(event: any) {
    this.innerWidth = window.innerWidth;
  }
  showMobile(routerOutlet: any) {
    this.innerWidth = window.innerWidth;
    if (this.innerWidth < 992) {
      return (
        routerOutlet &&
        routerOutlet.activatedRouteData &&
        routerOutlet.activatedRouteData['animation']
      );
    }
  }
  showDesktop(routerOutlet: any) {
    this.innerWidth = window.innerWidth;
    if (this.innerWidth > 991) {
      return (
        routerOutlet &&
        routerOutlet.activatedRouteData &&
        routerOutlet.activatedRouteData['animation']
      );
    }
  }

  public onClick(event: any, elementId: string): void {
    event.preventDefault();
    this.viewportScroller.scrollToAnchor(elementId);
  }
  ngOnDestroy() {
    // unsubscribe to cookieconsent observables to prevent memory leaks
    this.popupOpenSubscription?.unsubscribe();
    this.popupCloseSubscription?.unsubscribe();
    this.initializeSubscription?.unsubscribe();
    this.statusChangeSubscription?.unsubscribe();
    this.revokeChoiceSubscription?.unsubscribe();
    this.noCookieLawSubscription?.unsubscribe();

    this.checkSiteStatusInterval.unsubscribe();
  }

  checkSiteStatus() {
    this.settingsService.fetchAll().subscribe(
      (responseData) => {
        this.siteStatus = +responseData.online;
        if (this.siteStatus !== 1) {
          this.router.navigate(['offline']);
        }
      },
      (error) => {
        // console.log(error);
      }
    );
  }

  onCloseAward(event: any) {
    this.showAward = false;
    // any more to show?
    this.checkNotifications();
  }

  checkNotifications() {
    this.achievementService.getNotifications().subscribe((responseData) => {
      this.notifications = responseData;
      if (this.notifications.length > 0) {
        this.showAward = true;
      }
    });
  }

  checkGettingStarted() {
    let taskIds = [
      'welcomeVideo',
      'eq5dPre',
      'readGoals',
      // 'setGoal',
      'healthStatusBaseline',
      //'readingList',
      // 'createActionPlan',
      'readActivity',
      'exerciseSafetyQuiz',
    ];

    let safeUrls = [
      '/login',
      '/register',
      '/getting-started',
      '/getting-started/exercise',
      '/getting-started/goals',
      '/getting-started/health-status',
      '/getting-started/exercise-quiz',
      '/getting-started/eq5d',
      '/getting-started/reading-list',
      '/getting-started/action-plan',
      '/getting-started/terms',
      '/getting-started/privacy',
      '/getting-started/support',
      '/getting-started/ask-hcp',
      '/logout',
      '/change-password',
      '/chat',
      '/chat/healthcare-professional',
      '/chat/support',
      '/update-details',
      '/home',
      '/terms',
      '/privacy',
    ];

    if (
      safeUrls.indexOf(this.router.url) == -1 &&
      !this.gettingStartedCompleted
    ) {
      let tasksComplete = 0;
      let userTasksStatusById: any = {};
      taskIds.forEach((taskId) => {
        userTasksStatusById[taskId] = 0;
      });

      if (this.userData) {
        if (
          this.userData.primary_diagnosis &&
          taskIds.indexOf('createActionPlan') == -1
        ) {
          let diagnosisMode = this.userService.getDiagnosisMode(
            this.userData.primary_diagnosis
          );
          if (
            diagnosisMode == 'respiratory' ||
            this.userData.primary_diagnosis == 6 //HF
          ) {
            taskIds.push('createActionPlan');
          }
        }

        forkJoin({
          userTaskStatuses: this.userTaskStatusService.fetchAll(),
          // targets: this.userTargetService.fetchAll(),
        }).subscribe((responseData) => {
          let userTaskStatuses = responseData.userTaskStatuses;
          userTaskStatuses.forEach((userTaskStatus) => {
            userTasksStatusById[userTaskStatus.task_id] = userTaskStatus.status;
            if (taskIds.indexOf(userTaskStatus.task_id) != -1) {
              tasksComplete++;
            }
          });

          if (tasksComplete < taskIds.length) {
            // not completed
            this.router.navigateByUrl('/getting-started');
          } else {
            this.gettingStartedCompleted = true;
          }
        });
      }
    }
  }

  checkExpired() {
    if (this.userData) {
      var createdDate = moment(this.userData.created, 'YYYY-MM-DD');
      var now = moment();
      var difference = now.diff(createdDate, 'days');
      if (this.userData.is_admin != 1 && difference > 365) {
        this.userData.expired = true;
      }

      if (
        this.userData.expired &&
        this.router.url.indexOf('/resources') == -1
        && 
        this.router.url.indexOf('/bitesize/') == -1
        && 
        this.router.url.indexOf('/login') == -1
        && 
        this.router.url.indexOf('/getting-started/terms') == -1
        && 
        this.router.url.indexOf('/getting-started/privacy') == -1
        && 
        this.router.url.indexOf('/getting-started') == -1
      ) {
        this.router.navigateByUrl('/resources');
      }
    }
  }
}
